import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';
import MuiButton from '@mui/material/Button';
import LoginIcon from '@mui/icons-material/ExitToApp';
import DeleteIcon from '@mui/icons-material/Delete';

// core components
import Card from '../md/components/Card/Card.jsx';
import CardHeader from '../md/components/Card/CardHeader.jsx';
import CardBody from '../md/components/Card/CardBody.jsx';

import accountOverviewStyle from '../assets/jss/crm/views/accountOverviewStyle';

import { setUserInfo } from '../store/slices/userInfoSlice';
import { clearFirmInfo } from '../store/slices/firmInfoSlice';
import { pushLogin } from '../store/slices/loginsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';
import { updateDemo } from '../store/slices/accountsSlice';

import { createStdRequest, apiFetch, reportTime, showAlert } from '../utils/utils';

import ReduxLoader from '../store/ReduxLoader';
import KSpinner from '../components/KSpinner';
import AlertModal from '../components/AlertModal';

/*
const fancyStyle = {
  gradient: {
    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
    borderRadius: 3,
    border: 0,
    color: 'white',
    height: 26,
    padding: '0 6px',
    boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
  }}
  */

class DemoOverview extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dataIsReady: false,
      errorMessage: '',
      redirect: undefined,
      toHome: false,
      loading: false,
      toDemosListPage: false,
    };
  }

  componentDidMount() {
    this.fillInDataForDemo();
    this.setState({ dataIsReady: true });
  }

  fillInDataForDemo() {
    this.setState({ loading: true, dataIsReady: false });
    const idOfDemoToShow = this.props.idOfDemoToShow;
    const uri = `/api/demo/${idOfDemoToShow}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      this.props.updateDemo(json);
      this.setState({ loading: false, dataIsReady: true });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  deleteDemo = (demoId) => {
    const uri = '/api/demo/' + demoId;
    const request = createStdRequest(uri, 'DELETE');
    const actionOnGoodResponse = (json) => {
      this.props.clearFirmInfo();
      this.setState({ toDemosListPage: true });
    };
    apiFetch(this, request, actionOnGoodResponse);
  };

  logInToDemo(email) {
    console.log('Should now be logging in to demo');
    // Call /api/Account/LogInToDemo?email=XYZ
    // store the response's new user info in Redux
    // redirect to Home

    const b4Login = DateTime.local();
    const uri = '/api/Account/LogInToDemo?email=' + encodeURIComponent(email);
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      reportTime('Login', b4Login);
      const { success, userId, personId, email, phone, role, prefName, lastName, firmId, errorCode } = json;
      if (success) {
        this.setState({ loading: true });
        const loginObj = { loginType: 'toDemo', email };
        this.props.pushLogin(loginObj);
        const userInfo = { userId, personId, email, phone, role, prefName, lastName };
        this.props.setUserInfo(userInfo);
        ReduxLoader.loadFirmInfo(firmId, this, () => this.setStateToGoToNewUserHome());
      } else {
        // Possible problems, by error code:
        //   1) No such user name (email)
        //   2) Good user name, but not attached to a provider
        //   3) The associated provider is not part of a demo
        //   5) The demo firm has been deactivated
        switch (errorCode) {
          case 1:
            this.setState({ errorMessage: 'We have no account with that user name (email)' });
            break;
          case 2:
            this.setState({ errorMessage: 'The user is not associated with a provider' });
            break;
          case 3:
            this.setState({ errorMessage: "There is no demo associated with this user's provider" });
            break;
          case 5:
            this.setState({ errorMessage: 'This demo has been deactivated' });
            break;
          default:
            this.setState({ errorMessage: 'Unknown login error. Error code: ' + errorCode });
            break;
        }
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  setStateToGoToNewUserHome() {
    this.setState({ loading: false, toHome: true });
  }

  addReferringOrgs(firmId, count) {
    const uri = `/api/firm/${firmId}/visibleFirms/${count}`;
    const request = createStdRequest(uri, 'POST', {});
    const actionOnGoodResponse = () => {
      showAlert(this, 'Done');
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  addPractitioners(firmId, count) {
    showAlert(this, 'NOOP');
  }

  addClients(firmId, count) {
    showAlert(this, 'NOOP');
  }

  addTasks(firmId, count) {
    showAlert(this, 'NOOP');
  }

  render() {
    if (this.state.toHome) {
      return <Redirect to="/homeDispatcher" />;
    }

    if (this.state.errorMessage) {
      alert(this.state.errorMessage);
      this.setState({ errorMessage: '' });
    }

    if (this.state.toDemosListPage) {
      return <Redirect to="demos" />;
    }

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    if (this.state.loading) {
      return <KSpinner />;
    }

    if (!this.state.dataIsReady) {
      return <KSpinner />;
    }

    const { idOfDemoToShow, demos } = this.props;
    if (!idOfDemoToShow || idOfDemoToShow < 1) {
      return (
        <div>
          <h2>No demo selected to show.</h2>
          <div>ID of demo to show: {idOfDemoToShow}</div>
        </div>
      );
    }

    const matches = demos.filter((d) => d.demoId === idOfDemoToShow);
    if (matches.length !== 1) {
      if (matches.length === 0) {
        return (
          <div>
            <h2>No demo matches the specified ID.</h2>
            <div>ID of demo to show: {idOfDemoToShow}</div>
          </div>
        );
      } else {
        return (
          <div>
            <h2>Too many demos match the specified ID.</h2>
            <div>ID of demo to show: {idOfDemoToShow}</div>
          </div>
        );
      }
    }

    const { demoId, firmId, firmName, adminsNamesAndEmails, usersNamesAndEmails, deleteDate } = matches[0];
    const { classes } = this.props;
    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <Grid container>
          <Grid item xs={1} />
          <Grid item xs={10}>
            <Card>
              <CardHeader color="warning" stats icon>
                <h3 className={classes.cardTitle}>Demo Overview</h3>
              </CardHeader>
              <CardBody>
                <h4>Demo Account: {firmName}</h4>
                <div>
                  From this page, you can
                  <ul>
                    <li>log in as an Admin of this provider</li>
                    <li>log in as one of the regular Users at this provider</li>
                    <li>delete the demo</li>
                  </ul>
                </div>
                <hr />
                <div style={{ float: 'right' }}>
                  <table>
                    <tbody>
                      <tr>
                        <td>
                          <button onClick={() => this.addReferringOrgs(firmId, 1)}>Add 1 Referring Org</button>
                        </td>
                        <td>
                          <button onClick={() => this.addReferringOrgs(firmId, 30)}>Add 30 Referring Orgs</button>
                        </td>
                        <td>
                          <button onClick={() => this.addReferringOrgs(firmId, 300)}>Add 300 Referring Orgs</button>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <button onClick={() => this.addPractitioners(firmId, 1)}>Add 1 Practitioner</button>
                        </td>
                        <td>
                          <button onClick={() => this.addPractitioners(firmId, 30)}>Add 30 Practitioners</button>
                        </td>
                        <td>
                          <button onClick={() => this.addPractitioners(firmId, 300)}>Add 300 Practitioners</button>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <button onClick={() => this.addClients(firmId, 1)}>Add 1 Client</button>
                        </td>
                        <td>
                          <button onClick={() => this.addClients(firmId, 200)}>Add 200 Clients</button>
                        </td>
                        <td>
                          <button onClick={() => this.addClients(firmId, 2000)}>Add 2000 Clients</button>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <button onClick={() => this.addTasks(firmId, 1)}>Add 1 Task</button>
                        </td>
                        <td>
                          <button onClick={() => this.addTasks(firmId, 200)}>Add 200 Tasks</button>
                        </td>
                        <td>
                          <button onClick={() => this.addTasks(firmId, 2000)}>Add 2000 Tasks</button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <strong>Admins:</strong>
                  {adminsNamesAndEmails.map((admin) => (
                    <UserLine
                      key={admin.email}
                      name={admin.name}
                      email={admin.email}
                      logInToFunc={() => this.logInToDemo(admin.email)}
                    />
                  ))}
                </div>
                <div>
                  <br />
                  <strong>Users:</strong>
                  {usersNamesAndEmails.map((user) => (
                    <UserLine
                      key={user.email}
                      name={user.name}
                      email={user.email}
                      logInToFunc={() => this.logInToDemo(user.email)}
                    />
                  ))}
                </div>

                <div>
                  <br />
                  This demo Provider and all of its data will be deleted automatically on {deleteDate}, or you can{' '}
                  {'  '}
                  <MuiButton
                    className={this.props.classes.gradient}
                    size="small"
                    onClick={() => this.deleteDemo(demoId)}
                  >
                    <DeleteIcon />
                    Delete this demo now!
                  </MuiButton>
                </div>
              </CardBody>
            </Card>
          </Grid>
        </Grid>
      </div>
    );
  }
}

class UserLine extends Component {
  render() {
    const { name, email, logInToFunc } = this.props;
    return (
      <div>
        {name}
        {'  '}
        {email && (
          <MuiButton size="small" onClick={() => logInToFunc()}>
            <LoginIcon />
            Log In
          </MuiButton>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  idOfDemoToShow: state.intendedInfo.intendedDemoId,
  demos: state.accounts.demos,
});

const actionProducers = { setUserInfo, clearFirmInfo, pushLogin, setApiResponseInfo, updateDemo };

// export default connect(mapStateToProps, actionProducers) (withStyles(fancyStyle)(DemoOverview));
export default connect(mapStateToProps, actionProducers)(withStyles(accountOverviewStyle)(DemoOverview));
